import BaseAPI from './BaseAPI';
import cs from './const.js';
import AntLocalization from './AntLocalization';
import AntTime from './AntTime';
import hash from 'hash-sum';
import stringify from './stableStringify';
import AntOnboardingCache from './AntOnboardingCache';
import { enqueueSearchPrepare } from './AntSearchPrepareBatcher';
import {
    applyCollectionSnapshot,
    applyPreparedSnapshot,
    createCollectionSnapshotRequest
} from './AntSnapshotTransport';

const PREPARED_SNAPSHOT_TRANSPORT_VERSION = 2;

function getSearchPrepareCacheScope() {
    let info = localStorage.getItem(cs.System_Code + '-session-info');
    try {
        if (typeof info === 'string') info = JSON.parse(info);
    } catch (_error) {
        info = {};
    }
    info = info || {};
    return {
        business_code: info.business_code,
        business_short_code: info.business_short_code,
        user_code: info.user_code,
        entity_bus_code: info.entity_bus_code,
        store_code: info.store_code,
        locale: info.locale
    };
}

function getPreparedSnapshotBase(snapshot, id, code) {
    if (!snapshot || typeof snapshot !== 'object' || !snapshot.snapshotRevision) return null;
    if (id !== undefined && id !== null && String(snapshot.id) !== String(id)) return null;
    if (code !== undefined && code !== null && String(snapshot.code) !== String(code)) return null;
    return snapshot;
}

function decodePreparedPayload(payload, baseSnapshot) {
    if (!payload || !payload.snapshotTransport) return payload;
    const prepared = applyPreparedSnapshot(baseSnapshot, payload);
    if (!prepared) return null;
    if (payload.updated !== undefined) prepared.updated = payload.updated;
    return prepared;
}

export default class extends BaseAPI {
    constructor(moduleName, entityName, entityDisplay, defaultObj) {
        super();

        this.moduleName = moduleName;
        this.entityName = entityName;

        this.entityDisplay = AntLocalization.translateText(entityDisplay);
        this.routeURL = moduleName + '/' + entityName;
        this.defaultObj = defaultObj;
    }

    init(data) {
        if (data && data.entityDisplay) this.entityDisplay = AntLocalization.translateText(data.entityDisplay);
    }

    //---------------- SEARCH ----------------
    async postSearchPrepare(params, opts) {
        const lookupConds = params && typeof params === 'object' && !(params instanceof Array) ? { ...params } : {};
        if (!lookupConds.searchText) delete lookupConds.searchText;
        delete lookupConds.isSearching;
        delete lookupConds.minimizeData;
        delete lookupConds.orderBy;
        delete lookupConds.orderType;
        delete lookupConds.pageSize;
        delete lookupConds.pageIndex;
        delete lookupConds.selectFields;
        delete lookupConds.selectFields_search_inner_join;

        const freshness =
            (opts && (opts.freshness === 'realtime' || opts.caching === false || opts.byPassGetCache)) || lookupConds.byPassGetCache
                ? 'realtime'
                : 'cached';
        delete lookupConds.byPassGetCache;

        var body = { lookupConds };
        const cataloguePolicy = opts?.cachePolicy === 'ar-orders-catalogues-v1' ? opts.cachePolicy : undefined;
        if (opts && opts.selectLookups) body.selectLookups = opts.selectLookups;
        var URL = `/${this.routeURL}/search/prepare`;

        // The opt-in lookup is cached by Redis after resolving permissions. A
        // browser copy would conceal invalidations/new categories for its TTL.
        var isCaching = !cataloguePolicy && freshness === 'cached' && !AntOnboardingCache.isFresh(this.entityName);
        var staleCachedData = null;
        var hashKey = null;
        const cacheScope = getSearchPrepareCacheScope();

        if (isCaching) {
            if (!body.lookupConds || !body.lookupConds.searchText) {
                hashKey =
                    cs.System_Code +
                    '-search-prepare-' +
                    hash(
                        stringify({
                            body: body,
                            URL: URL,
                            cacheScope,
                            onboardingCacheGroup: AntOnboardingCache.cacheGroup('', this.entityName)
                        })
                    );
                let data = localStorage.getItem(hashKey);
                if (data) data = JSON.parse(data);
                if (data && data.data !== undefined) staleCachedData = data.data;
                if (data && data.queryAt && AntTime.isUnixWithinMinutes(data.queryAt, isCaching ? 60 * 24 * 7 : 60 * 24)) return data.data;
            }
        }

        const hasSelectedLookups = body.selectLookups instanceof Array && body.selectLookups.length > 0;
        const result = hasSelectedLookups
            ? await enqueueSearchPrepare(this, {
                  lookupConds,
                  selectLookups: body.selectLookups,
                  freshness,
                  cachePolicy: cataloguePolicy,
                  isSetting: Boolean((opts && opts.isSetting) || lookupConds.isSetting),
                  scopeKey: stringify(cacheScope)
              })
            : await new Promise((resolve) => {
                  this.post(
                      URL,
                      { ...body, byPassGetCache: freshness === 'realtime' ? true : undefined },
                      (response) => resolve(response && response.data ? response.data : null)
                  );
              });
        if (!result) return staleCachedData;

        if (isCaching && hashKey) localStorage.setItem(hashKey, stringify({ data: result, queryAt: AntTime.nowUnix() }));
        return result;
    }

    async getSearch(params, opts) {
        return this.getList(params, null, opts);
    }

    //---------------- GET ----------------
    async getList(params, url, opts) {
        var fields = [{ name: 'total', value: !Boolean(opts && opts.byPassTotal) }];

        var that = this;
        if ((!opts || opts.byPassDefaultConds) && this.defaultObj) {
            if (this.defaultObj instanceof Array) fields.push(...this.defaultObj);
            else if (typeof this.defaultObj === 'object') {
                Object.keys(this.defaultObj).forEach(function (key) {
                    if (that.defaultObj[key] !== undefined) fields.push({ name: key, value: that.defaultObj[key] });
                });
            }
        }

        if (params) {
            if (params instanceof Array) fields.push(...params);
            else if (typeof params === 'object') {
                Object.keys(params).forEach(function (key) {
                    if (params[key] !== undefined) fields.push({ name: key, value: params[key] });
                });
            }
        }

        var body = {};

        fields = fields
            .filter((p) => p.name && p.value !== undefined)
            .forEach((p) => {
                body[p.name] = p.value;
            });

        var URL = `/${this.routeURL}/list`;
        if (url) {
            URL = url;
            if (String(URL).startsWith('/') === false) URL = '/' + URL;
        }

        if (body.selectFields instanceof Array && body.selectFields.length > 0) body.minimizeData = true;

        return new Promise((resolve) => {
            that.post(URL, body, (obj) => {
                var rows = [];
                var totalCount = 0;
                var summary = null;
                var queried = false;

                if (obj && obj.data && obj.data.summary) summary = obj.data.summary;

                if (obj && obj.data && obj.data.rows instanceof Array) {
                    rows = obj.data.rows;
                    totalCount = obj.data.totalCount;
                    queried = true;
                } else if (obj && obj.data instanceof Array) {
                    rows = obj.data;
                    totalCount = obj.data.length;
                    queried = true;
                }

                if (
                    body.selectFields instanceof Array &&
                    body.selectFields.length > 0 &&
                    rows.length > 0 &&
                    Object.keys(rows[0]).some((rK) => String(rK).startsWith('f_'))
                ) {
                    for (let i = 0; i < rows.length; i++) {
                        let row = rows[i];

                        for (let j = 0; j < body.selectFields.length; j++) {
                            const fName = body.selectFields[j];
                            let value = row['f_' + j];
                            row[fName] = value;
                            delete row['f_' + j];
                        }

                        row.row_index = i;
                    }
                }
                try {
                    if (that.customData) rows = rows.map((r) => that.customData(r));
                } catch (error) {
                    console.log('API-getList-error : ', error);
                }

                resolve({
                    summary: summary,
                    rows: rows,
                    totalCount: totalCount,
                    queried: queried
                });
            });
        });
    }

    async getDetail(id) {
        var body = { id: id };

        var URL = `/${this.routeURL}/detail`;

        var that = this;
        return new Promise((resolve) => {
            that.post(URL, body, (obj) => {
                resolve(obj);
            });
        });
    }

    //---------------- COUNT ----------------
    async getCount(params, url) {
        var fields = [];

        var that = this;
        if (this.defaultObj) {
            if (this.defaultObj instanceof Array) fields.push(...this.defaultObj);
            else if (typeof this.defaultObj == 'object') {
                Object.keys(this.defaultObj).forEach(function (key) {
                    if (that.defaultObj[key] !== undefined) fields.push({ name: key, value: that.defaultObj[key] });
                });
            }
        }

        if (params) {
            if (params instanceof Array) fields.push(...params);
            else if (typeof params == 'object') {
                Object.keys(params).forEach(function (key) {
                    if (params[key] !== undefined) fields.push({ name: key, value: params[key] });
                });
            }
        }

        var body = {};

        fields = fields
            .filter((p) => p.name && p.value !== undefined)
            .forEach((p) => {
                body[p.name] = p.value;
            });

        var URL = `/${this.routeURL}/count`;
        if (url) {
            URL = url;
            if (String(URL).startsWith('/') === false) URL = '/' + URL;
        }

        return new Promise((resolve) => {
            that.post(URL, body, (obj) => {
                resolve(obj);
            });
        });
    }
    async getCounts(params, url) {
        var fields = [];

        var that = this;
        if (this.defaultObj) {
            if (this.defaultObj instanceof Array) fields.push(...this.defaultObj);
            else if (typeof this.defaultObj == 'object') {
                Object.keys(this.defaultObj).forEach(function (key) {
                    if (that.defaultObj[key] !== undefined) fields.push({ name: key, value: that.defaultObj[key] });
                });
            }
        }

        if (params) {
            if (params instanceof Array) fields.push(...params);
            else if (typeof params == 'object') {
                Object.keys(params).forEach(function (key) {
                    if (params[key] !== undefined) fields.push({ name: key, value: params[key] });
                });
            }
        }

        var body = {};

        fields = fields
            .filter((p) => p.name && p.value !== undefined)
            .forEach((p) => {
                body[p.name] = p.value;
            });

        var URL = `/${this.routeURL}/counts`;
        if (url) {
            URL = url;
            if (String(URL).startsWith('/') === false) URL = '/' + URL;
        }

        return new Promise((resolve) => {
            that.post(URL, body, (obj) => {
                resolve(obj);
            });
        });
    }
    //---------------- SUM ----------------
    async getSum(params, sumField, url) {
        var fields = [];

        var that = this;
        if (this.defaultObj) {
            if (this.defaultObj instanceof Array) fields.push(...this.defaultObj);
            else if (typeof this.defaultObj == 'object') {
                Object.keys(this.defaultObj).forEach(function (key) {
                    if (that.defaultObj[key] !== undefined) fields.push({ name: key, value: that.defaultObj[key] });
                });
            }
        }

        if (params) {
            if (params instanceof Array) fields.push(...params);
            else if (typeof params == 'object') {
                Object.keys(params).forEach(function (key) {
                    if (params[key] !== undefined) fields.push({ name: key, value: params[key] });
                });
            }
        }

        var body = { sumField: sumField };

        fields = fields
            .filter((p) => p.name && p.value !== undefined)
            .forEach((p) => {
                body[p.name] = p.value;
            });

        var URL = `/${this.routeURL}/sum`;
        if (url) {
            URL = url;
            if (String(URL).startsWith('/') === false) URL = '/' + URL;
        }

        return new Promise((resolve) => {
            that.post(URL, body, (obj) => {
                resolve(obj);
            });
        });
    }

    //---------------- DOWNLOAD ----------------
    async downloadExcel(params, url) {
        // console.log('ant-log-params : ', params);
        // console.log('ant-log-url : ', url);
        var fields = [];

        var that = this;
        if (this.defaultObj) {
            if (this.defaultObj instanceof Array) fields.push(...this.defaultObj);
            else if (typeof this.defaultObj == 'object') {
                Object.keys(this.defaultObj).forEach(function (key) {
                    if (that.defaultObj[key] !== undefined) fields.push({ name: key, value: that.defaultObj[key] });
                });
            }
        }

        if (params) {
            if (params instanceof Array) fields.push(...params);
            else if (typeof params == 'object') {
                Object.keys(params).forEach(function (key) {
                    if (params[key] !== undefined) fields.push({ name: key, value: params[key] });
                });
            }
        }

        var body = {};

        fields = fields
            .filter((p) => p.name && p.value !== undefined)
            .forEach((p) => {
                body[p.name] = p.value;
            });

        if (String(url).endsWith('/list')) url = String(url).slice(0, -5);
        else if (String(url).endsWith('/list/')) url = String(url).slice(0, -6);

        var URL = `/${this.routeURL}/download/excel`;
        if (url) {
            URL = `${url}/download/excel`;
            if (String(URL).startsWith('/') === false) URL = '/' + URL;
        }

        return new Promise((resolve) => {
            that.download(URL, body, `${this.entityDisplay}.xlsx`, () => {
                resolve();
            });
        });
    }

    //---------------- VALIDATION ----------------
    async postValidation(obj) {
        var body = obj;

        var URL = `/${this.routeURL}/input/validate`;

        var that = this;
        return new Promise((resolve) => {
            that.post(URL, body, (data) => {
                resolve(data);
            });
        });
    }

    //---------------- CREATE ----------------
    async postCreatePrepare(obj, params) {
        var body = obj;

        if (params) body = { ...body, ...params };

        var URL = `/${this.routeURL}/create/prepare`;
        var that = this;
        return new Promise((resolve) => {
            that.post(URL, body, (data) => {
                if (data && data.data) resolve(data.data);
                else resolve(null);
            });
        });
    }

    async postCreateValidation(obj, url) {
        var body = obj;

        var URL = `/${this.routeURL}/create/validate`;
        if (url) {
            URL = url;
            if (String(URL).startsWith('/') === false) URL = '/' + URL;
        }

        var that = this;
        return new Promise((resolve) => {
            that.post(URL, body, (data) => {
                resolve(data);
            });
        });
    }

    async postCreate(obj, opts) {
        var body = obj;

        var URL = `/${this.routeURL}/create`;
        if (opts && opts.url) {
            URL = opts.url;
            if (String(URL).startsWith('/') === false) URL = '/' + URL;
        }

        var that = this;
        return new Promise((resolve) => {
            that.post(URL, body, (data) => {
                resolve(data);
            });
        });
    }

    async postQuickCreate(obj) {
        var body = { ...obj, isQuickCreate: true };

        var URL = `/${this.routeURL}/create`;

        var that = this;
        return new Promise((resolve) => {
            that.post(URL, body, (data) => {
                resolve(data);
            });
        });
    }

    async postScanBarcode(obj) {
        var body = {};

        var fields = [];

        var that = this;

        if (obj) {
            if (obj instanceof Array) fields.push(...obj);
            else if (typeof obj === 'object') {
                Object.keys(obj).forEach(function (key) {
                    if (obj[key] !== undefined) fields.push({ name: key, value: obj[key] });
                });
            }
        }

        fields = fields
            .filter((p) => p.name && p.value !== undefined)
            .forEach((p) => {
                body[p.name] = p.value;
            });

        var URL = `/${this.routeURL}/scanBarcode`;

        var that = this;
        return new Promise((resolve) => {
            that.post(URL, body, (data) => {
                resolve(data);
            });
        });
    }

    //---------------- UPDATE ----------------

    async postGetChecksum(id, code) {
        var body = {
            id: id !== undefined && id !== null ? id : undefined,
            code: code !== undefined && code !== null ? code : undefined
        };

        var URL = `/${this.routeURL}/update/checksum`;
        var that = this;
        return new Promise((resolve) => {
            that.post(URL, body, (data) => {
                if (data && data.data) resolve(data.data);
                else resolve(null);
            });
        });
    }

    async postUpdatePrepare(id, code, recalcable, params, currentSnapshot) {
        var body = {
            id: id !== undefined && id !== null ? id : undefined,
            code: code !== undefined && code !== null ? code : undefined,
            recalcable: Boolean(recalcable)
        };

        if (params) body = { ...body, ...params };
        const baseSnapshot = getPreparedSnapshotBase(currentSnapshot, id, code);
        if (baseSnapshot) {
            body.knownSnapshotRevision = baseSnapshot.snapshotRevision;
            body.snapshotTransportVersion = PREPARED_SNAPSHOT_TRANSPORT_VERSION;
        } else {
            delete body.knownSnapshotRevision;
            delete body.snapshotTransportVersion;
        }

        var that = this;
        if (this.defaultObj && typeof this.defaultObj == 'object') {
            Object.keys(this.defaultObj).forEach(function (key) {
                if (that.defaultObj[key] !== undefined) body[key] = that.defaultObj[key];
            });
        }

        var URL = `/${this.routeURL}/update/prepare`;
        var that = this;
        return new Promise((resolve) => {
            that.post(URL, body, async (data) => {
                if (data && data.error) resolve({ error: data.error, code: data.code });
                else if (data && data.data) {
                    const prepared = decodePreparedPayload(data.data, baseSnapshot);
                    if (prepared) resolve(prepared);
                    else resolve(await that.postUpdatePrepare(id, code, recalcable, params));
                }
                else resolve(null);
            });
        });
    }

    async postUpdateValidation(obj, newData, url) {
        var body = {
            where: { id: obj.id, code: obj.code, checksum: obj.checksum },
            update: { ...newData }
        };

        var that = this;
        if (this.defaultObj && typeof this.defaultObj == 'object') {
            Object.keys(this.defaultObj).forEach(function (key) {
                if (that.defaultObj[key] !== undefined) body[key] = that.defaultObj[key];
            });
        }

        var URL = `/${this.routeURL}/update/validate`;
        if (url) {
            URL = url;
            if (String(URL).startsWith('/') === false) URL = '/' + URL;
        }

        var that = this;
        return new Promise((resolve) => {
            that.post(URL, body, (data) => {
                resolve(data);
            });
        });
    }

    async postUpdate(obj, newData, opts, currentSnapshot) {
        var body = {
            where: { ...obj, id: obj.id, code: obj.code, checksum: obj.checksum },
            update: { ...newData },
            options: opts
        };
        const baseSnapshot = opts && opts.getNewPrepare ? getPreparedSnapshotBase(currentSnapshot, obj.id, obj.code) : null;
        if (baseSnapshot) {
            body.knownSnapshotRevision = baseSnapshot.snapshotRevision;
            body.snapshotTransportVersion = PREPARED_SNAPSHOT_TRANSPORT_VERSION;
        }

        var that = this;
        if (this.defaultObj && typeof this.defaultObj == 'object') {
            Object.keys(this.defaultObj).forEach(function (key) {
                if (that.defaultObj[key] !== undefined) body[key] = that.defaultObj[key];
            });
        }

        var URL = `/${this.routeURL}/update`;
        if (opts && opts.url) {
            URL = opts.url;
            if (String(URL).startsWith('/') === false) URL = '/' + URL;
        }

        var that = this;
        return new Promise((resolve) => {
            that.post(URL, body, async (data) => {
                if (data && data.data && data.data.snapshotTransport) {
                    const prepared = decodePreparedPayload(data.data, baseSnapshot);
                    if (prepared) data = { ...data, data: prepared };
                    else {
                        const fallback = await that.postUpdatePrepare(obj.id, obj.code, false);
                        if (fallback) data = { ...data, data: { ...fallback, updated: data.data.updated } };
                    }
                }
                resolve(data);
            });
        });
    }

    //---------------- CLONE ----------------
    async postClone(code, id) {
        var body = {
            code: code !== undefined && code !== null ? code : undefined,
            id: id !== undefined && id !== null ? id : undefined
        };
        var URL = `/${this.routeURL}/clone`;
        var that = this;
        return new Promise((resolve) => {
            that.post(URL, body, (data) => {
                resolve(data);
            });
        });
    }

    //---------------- DELETE ----------------
    async postDeletePrepare(objs) {
        var body = {
            ids: objs.map((o) => o.id)
        };

        var URL = `/${this.routeURL}/delete/prepare`;
        var that = this;
        return new Promise((resolve) => {
            that.post(URL, body, (obj) => {
                if (obj && obj.data && obj.data.rows && obj.data.rows instanceof Array)
                    resolve({
                        rows: obj.data.rows
                    });
                else
                    resolve({
                        rows: []
                    });
            });
        });
    }

    async postDelete(objs) {
        var body = {
            where: { ids: objs.map((o) => o.id) }
        };

        var URL = `/${this.routeURL}/delete`;
        var that = this;
        return new Promise((resolve) => {
            that.post(URL, body, (data) => {
                resolve(data);
            });
        });
    }

    //---------------- BATCH-CREATE ----------------

    async postBatchCreatePrepare(obj) {
        var body = { ...obj };
        var URL = `/${this.routeURL}/batch-create/prepare`;
        var that = this;
        return new Promise((resolve) => {
            that.post(URL, body, (data) => {
                if (data && data.data) resolve(data.data);
                else resolve(null);
            });
        });
    }

    async postBatchCreateValidation(objs) {
        var body = { objs: [...objs] };

        var URL = `/${this.routeURL}/batch-create/validate`;

        var that = this;
        return new Promise((resolve) => {
            that.post(URL, body, (obj) => {
                if (obj && obj.data && obj.data.rows && obj.data.rows instanceof Array)
                    resolve({
                        error: obj.error,
                        rows: obj.data.rows
                    });
                else if (obj && obj.data && obj.data instanceof Array)
                    resolve({
                        error: obj.error,
                        rows: obj.data
                    });
                else
                    resolve({
                        error: obj.error,
                        rows: []
                    });
            });
        });
    }

    async postBatchCreate(objs) {
        var body = { objs: [...objs] };

        var URL = `/${this.routeURL}/batch-create`;

        var that = this;
        return new Promise((resolve) => {
            that.post(URL, body, (data) => {
                resolve(data);
            });
        });
    }

    //---------------- BATCH-UPDATE ----------------

    async postBatchUpdatePrepare(obj) {
        var body = {
            ...obj
        };

        var that = this;
        if (this.defaultObj && typeof this.defaultObj == 'object') {
            Object.keys(this.defaultObj).forEach(function (key) {
                if (that.defaultObj[key] !== undefined) body[key] = that.defaultObj[key];
            });
        }

        var URL = `/${this.routeURL}/batch-update/prepare`;
        var that = this;
        return new Promise((resolve) => {
            that.post(URL, body, (data) => {
                if (data && data.data) resolve(data.data);
                else resolve(null);
            });
        });
    }

    async postBatchUpdateValidation(objs) {
        var body = { objs: [...objs] };

        var URL = `/${this.routeURL}/batch-update/validate`;

        var that = this;
        return new Promise((resolve) => {
            that.post(URL, body, (obj) => {
                if (obj && obj.data && obj.data.rows && obj.data.rows instanceof Array)
                    resolve({
                        error: obj.error,
                        rows: obj.data.rows
                    });
                else if (obj && obj.data && obj.data instanceof Array)
                    resolve({
                        error: obj.error,
                        rows: obj.data
                    });
                else
                    resolve({
                        error: obj.error,
                        rows: []
                    });
            });
        });
    }

    async postBatchUpdate(objs, extras) {
        var body = { objs: [...objs], ...(extras || {}) };

        var URL = `/${this.routeURL}/batch-update`;

        var that = this;
        return new Promise((resolve) => {
            that.post(URL, body, (data) => {
                resolve(data);
            });
        });
    }

    async postBatchExecutePrepare(params) {
        var body = { ...(params || {}) };

        var URL = `/${this.routeURL}/batch-execute/prepare`;

        var that = this;
        return new Promise((resolve) => {
            that.post(URL, body, (result) => {
                if (result && result.data) resolve(result.data);
                else resolve(result);
            });
        });
    }

    async postBatchExecute(input) {
        var body = input instanceof Array ? { objs: [...input] } : { ...(input || {}) };

        var URL = `/${this.routeURL}/batch-execute`;

        var that = this;
        return new Promise((resolve) => {
            that.post(URL, body, (data) => {
                resolve(data);
            });
        });
    }

    //---------------- IMPORT ----------------

    async downloadImportSample(params, url) {
        var fields = [];

        var that = this;
        if (this.defaultObj) {
            if (this.defaultObj instanceof Array) fields.push(...this.defaultObj);
            else if (typeof this.defaultObj == 'object') {
                Object.keys(this.defaultObj).forEach(function (key) {
                    if (that.defaultObj[key] !== undefined) fields.push({ name: key, value: that.defaultObj[key] });
                });
            }
        }

        if (params) {
            if (params instanceof Array) fields.push(...params);
            else if (typeof params == 'object') {
                Object.keys(params).forEach(function (key) {
                    if (params[key] !== undefined) fields.push({ name: key, value: params[key] });
                });
            }
        }

        var body = {};

        fields = fields
            .filter((p) => p.name && p.value !== undefined)
            .forEach((p) => {
                body[p.name] = p.value;
            });

        var URL = `/${this.routeURL}/import/downloadSample`;
        if (url) {
            URL = url;
            if (String(URL).startsWith('/') === false) URL = '/' + URL;
        }

        return new Promise((resolve) => {
            that.download(URL, body, `import-${this.entityName}.xlsx`, () => {
                resolve();
            });
        });
    }

    async postImportValidation(objs, mode) {
        var body = { objs: [...objs], mode: mode };

        var URL = `/${this.routeURL}/import/validate`;

        var that = this;
        return new Promise((resolve) => {
            that.post(URL, body, (obj) => {
                if (obj && obj.data && obj.data.rows && obj.data.rows instanceof Array)
                    resolve({
                        rows: obj.data.rows
                    });
                else if (obj && obj.data && obj.data instanceof Array)
                    resolve({
                        rows: obj.data
                    });
                else
                    resolve({
                        rows: []
                    });
            });
        });
    }

    async postImport(objs, mode) {
        var body = { objs: [...objs], mode: mode };

        var URL = `/${this.routeURL}/import/upload`;

        var that = this;
        return new Promise((resolve) => {
            that.post(URL, body, (data) => {
                resolve(data);
            });
        });
    }

    //---------------- LOOKUP ----------------
    async postLookup(params, opts) {
        var fields = [{ name: 'total', value: !Boolean(opts && opts.byPassTotal) }];

        if (params) {
            if (params instanceof Array) fields.push(...params);
            else if (typeof params == 'object') {
                Object.keys(params).forEach(function (key) {
                    if (params[key] !== undefined) fields.push({ name: key, value: params[key] });
                });
            }
        }

        var body = {};

        fields = fields
            .filter((p) => p.name && p.value !== undefined)
            .forEach((p) => {
                body[p.name] = p.value;
            });

        var URL = `/${this.routeURL}/lookup`;

        var that = this;
        return new Promise((resolve) => {
            that.post(URL, body, (obj) => {
                if (obj && obj.data && obj.data.rows instanceof Array)
                    resolve({
                        rows: obj.data.rows,
                        totalCount: obj.data.totalCount
                    });
                else if (obj && obj.data instanceof Array)
                    resolve({
                        rows: obj.data,
                        totalCount: obj.data.length
                    });
                else
                    resolve({
                        rows: [],
                        totalCount: 0
                    });
            });
        });
    }

    //---------------- ACTION ----------------

    async postAsync(URL, body) {
        var that = this;
        return new Promise((resolve) => {
            that.post(URL, body, (data) => {
                resolve(data);
            });
        });
    }

    // ---- Address (SysAddress) — Plan 3: FE nạp dữ liệu địa chỉ runtime ----
    async getAddressCountries() {
        var that = this;
        return new Promise((resolve) => {
            that.post('/sys/SysAddress/getCountries', {}, (d) => resolve(d));
        });
    }
    async getAddressFile(country, knownVersion) {
        var that = this;
        return new Promise((resolve) => {
            that.post('/sys/SysAddress/getFile', { country: country, knownVersion: knownVersion }, (d) => resolve(d));
        });
    }
    async getAddressFileUrl(country) {
        var that = this;
        return new Promise((resolve) => {
            that.post('/sys/SysAddress/getFileUrl', { country: country }, (d) => resolve(d));
        });
    }

    // `prepareOpts` = đúng bộ tham số mà postUpdatePrepare vẫn gửi. Tuyến nào khai
    // `updatePrepare: true` sẽ chạy actionUpdatePrepare với nguyên thân yêu cầu, nên gửi kèm
    // thì phản hồi của hành động đã là phiếu đầy đủ, khỏi nạp lại phiếu thêm lượt nữa.
    async postAction(action, code, id, useQuickResponse, currentSnapshotOrRevision, prepareOpts) {
        const baseSnapshot = getPreparedSnapshotBase(currentSnapshotOrRevision, id, code);
        const knownSnapshotRevision = baseSnapshot
            ? baseSnapshot.snapshotRevision
            : typeof currentSnapshotOrRevision === 'string'
              ? currentSnapshotOrRevision
              : undefined;
        var body = {
            code: code !== undefined && code !== null ? code : undefined,
            id: id !== undefined && id !== null ? id : undefined,
            useQuickResponse: useQuickResponse,
            knownSnapshotRevision: knownSnapshotRevision || undefined,
            snapshotTransportVersion: PREPARED_SNAPSHOT_TRANSPORT_VERSION
        };

        // Chỉ chuyển tiếp các khoá điều khiển của prepare (không phải tên cột), để thân yêu cầu
        // của hành động không vô tình mang thêm điều kiện lọc.
        if (prepareOpts && typeof prepareOpts === 'object') {
            ['childName', 'childFields', 'childSearchConds', 'childActions', 'isSetting'].forEach((key) => {
                if (prepareOpts[key] !== undefined && body[key] === undefined) body[key] = prepareOpts[key];
            });
        }

        var URL = `/${this.routeURL}/${action}`;
        var that = this;
        return new Promise((resolve) => {
            that.post(URL, body, async (data) => {
                if (data && data.data && data.data.snapshotTransport) {
                    const prepared = decodePreparedPayload(data.data, baseSnapshot);
                    if (prepared) data = { ...data, data: prepared };
                    else {
                        const fallback = await that.postUpdatePrepare(id, code, false);
                        if (fallback) data = { ...data, data: fallback };
                    }
                }
                resolve(data);
            });
        });
    }

    async postCustomValidation(obj, url) {
        var body = obj;

        var URL = `${url}`;
        if (String(URL).startsWith('/') === false) URL = '/' + URL;

        var that = this;
        return new Promise((resolve) => {
            that.post(URL, body, (data) => {
                resolve(data);
            });
        });
    }

    async postCustomAction(obj, opts) {
        var body = obj;

        var URL = `/${this.routeURL}/create`;
        if (opts && opts.url) {
            URL = opts.url;
            if (String(URL).startsWith('/') === false) URL = '/' + URL;
        }

        var that = this;
        return new Promise((resolve) => {
            that.post(URL, body, (data) => {
                resolve(data);
            });
        });
    }

    async postActionWithData(action, data) {
        var body = {
            ...data
        };

        var URL = `/${this.routeURL}/${action}`;
        var that = this;
        return new Promise((resolve) => {
            that.post(URL, body, (data) => {
                resolve(data);
            });
        });
    }

    async postCollectionSnapshot(action, data, currentState = {}, options = {}) {
        const requestData = { ...(data || {}) };
        delete requestData.knownSnapshotRevision;
        delete requestData.knownRows;
        delete requestData.snapshotTransportVersion;
        const requestSnapshot = createCollectionSnapshotRequest(currentState, {
            forceFull: options.forceFull === true,
            snapshotTransportVersion: options.snapshotTransportVersion || 1
        });
        const response = await this.postActionWithData(action, {
            ...requestData,
            ...requestSnapshot
        });
        const payload = response?.data?.data || response?.data || response;
        const nextState = applyCollectionSnapshot(currentState, payload, options);

        // A delta can arrive after the caller has already replaced its local
        // baseline. Retry once without a manifest; never clear visible rows
        // merely because a delta could not be applied.
        if (!nextState && payload?.snapshotTransport?.startsWith('collection-') && options.forceFull !== true) {
            return await this.postCollectionSnapshot(action, requestData, {}, { ...options, forceFull: true });
        }

        // Keep this helper backward compatible while BE images are rolled out.
        // A legacy action can still return { rows }, but it cannot participate
        // in later delta requests until it returns a collection envelope.
        if (!nextState) {
            return {
                state: null,
                rows: payload?.rows instanceof Array ? payload.rows : payload instanceof Array ? payload : [],
                payload,
                isLegacy: !payload?.snapshotTransport
            };
        }

        return {
            ...nextState,
            state: nextState,
            payload,
            isLegacy: false
        };
    }

    async postConfirmOTPRequest(code, id, action, params) {
        var body = {
            code: code !== undefined && code !== null ? code : undefined,
            id: id !== undefined && id !== null ? id : undefined,
            action: action,
            params: params
        };

        var URL = `/${this.routeURL}/confirm-otp/request`;
        var that = this;
        return new Promise((resolve) => {
            that.post(URL, body, (data) => {
                resolve(data);
            });
        });
    }

    async postConfirmOTPVerify(code, id, action, token, otp) {
        var body = {
            code: code !== undefined && code !== null ? code : undefined,
            id: id !== undefined && id !== null ? id : undefined,
            action: action,
            token: token,
            otp: otp
        };

        var URL = `/${this.routeURL}/confirm-otp/verify`;
        var that = this;
        return new Promise((resolve) => {
            that.post(URL, body, (data) => {
                resolve(data);
            });
        });
    }
}
